import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Activity, TrendingDown, TrendingUp, BarChart3, Calculator, Users,
  AlertTriangle, CheckCircle, ArrowRight, Info, Target, DollarSign,
  XCircle, Clock, Package, HeadphonesIcon
} from 'lucide-react';

export default function DocsPromiseHealth() {
  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">
          Promise Health
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Promise Health Dashboard
        </h1>
        <p className="text-lg text-[#94A3B8] mb-6">
          Monitor and improve your promise-keeping performance — cancellations, backorders, 
          and late/partial fulfillment. See what's breaking, why it's happening, and what it's costing you.
        </p>
        <div className="flex gap-4">
          <Link to="/dashboard/promise-health">
            <Button className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="go-to-promise-health">
              Open Promise Health <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* What is Promise Health */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          What is Promise Health?
        </h2>
        <div className="bg-[#0D1628] border border-[#22304A] rounded-lg p-6 mb-6">
          <p className="text-[#94A3B8] mb-4">
            Commerce runs on promises: "In stock." "Delivers by Friday." "Ready for pickup."
          </p>
          <p className="text-[#94A3B8] mb-4">
            When those promises break, you don't just lose an order — you lose trust, time, and margin.
          </p>
          <p className="text-[#94A3B8]">
            <strong className="text-white">Promise Health</strong> makes promise-keeping measurable with:
          </p>
          <ul className="mt-4 space-y-2 text-[#94A3B8]">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              A Promise Integrity Score (0-100)
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              The drivers behind breakage
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              A benchmark against peers
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              An impact estimate that quantifies leakage
            </li>
          </ul>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Key Metrics Explained
        </h2>
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <Activity className="w-6 h-6 text-emerald-400" />
                <CardTitle className="text-white">Promise Integrity Score</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8]">
                A 0–100 score based on weighted promise breaks over your selected window. Higher is better.
                Severe events (cancellations, hard backorders, late/partial) weigh more than minor issues.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-sky-400" />
                <CardTitle className="text-white">Total Promises</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8]">
                Number of orders observed in this window. Each order represents a customer promise 
                (availability, delivery, or pickup expectation).
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
                <CardTitle className="text-white">Broken Rate</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8]">
                Weighted promise breaks per promise. Severity-adjusted: high-impact events count more 
                than low-impact events.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-rose-400" />
                <CardTitle className="text-white">Urgent Miss Rate</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8]">
                Percentage of urgency-tagged orders that missed their stated deadline. 
                This requires urgency tagging (e.g., gift/life-event deadline) to be enabled.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Dashboard Tabs */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Dashboard Tabs
        </h2>
        
        <div className="space-y-6">
          {/* Top Drivers */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-rose-400" />
                Top Drivers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] mb-4">
                <strong className="text-white">What's breaking?</strong> The biggest contributors to promise breaks in this window.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-[#1E293B] rounded-lg">
                  <XCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-white font-medium">Cancellations</h4>
                    <p className="text-sm text-[#94A3B8]">
                      Orders canceled after confirmation — high trust and margin damage.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#1E293B] rounded-lg">
                  <Package className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-white font-medium">Backorders</h4>
                    <p className="text-sm text-[#94A3B8]">
                      Items sold without reliable availability — a leading indicator of inventory promise issues.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#1E293B] rounded-lg">
                  <Clock className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-white font-medium">Late / Partial</h4>
                    <p className="text-sm text-[#94A3B8]">
                      Partial shipments or late fulfillment that trigger repeat contacts, refunds, or churn risk.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#1E293B] rounded-lg">
                  <HeadphonesIcon className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-white font-medium">Support Cost</h4>
                    <p className="text-sm text-[#94A3B8]">
                      Estimated support burden driven by disappointment events. Modeled using your assumptions.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Playbooks */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-400" />
                Playbooks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] mb-4">
                <strong className="text-white">What do I do next?</strong> Actionable recommendations based on your top drivers.
              </p>
              <p className="text-sm text-[#94A3B8]">
                Each playbook shows why it matters and provides recommended actions. You can:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-[#94A3B8]">
                <li>• <strong className="text-white">Mark as Adopted</strong> — We'll track your score and trends after this change</li>
                <li>• <strong className="text-white">Create Task</strong> — Send this playbook to your team</li>
                <li>• <strong className="text-white">Request Slack Approval</strong> — Route exception approvals into Slack</li>
              </ul>
            </CardContent>
          </Card>

          {/* Trends */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-sky-400" />
                Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] mb-4">
                <strong className="text-white">Disappointment trends over time.</strong> Track promise breaks by severity and driver.
              </p>
              <div className="space-y-2 text-sm text-[#94A3B8]">
                <p><strong className="text-white">Events Over Time:</strong> Promise breaks by severity (low/med/high)</p>
                <p><strong className="text-white">Driver Distribution:</strong> Which drivers dominate your breakage profile</p>
              </div>
              <div className="mt-4 p-3 bg-[#1E293B] rounded-lg">
                <p className="text-xs text-[#64748B]">
                  <strong>Severity Guide:</strong> High = strongest trust/margin damage • 
                  Medium = meaningful operational friction • Low = minor but worth reducing at scale
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Benchmark */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Benchmark
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] mb-4">
                <strong className="text-white">How do you compare?</strong> Compare your promise health to similar merchants (privacy-safe cohorts).
              </p>
              <p className="text-sm text-[#94A3B8] mb-4">
                Your position shows whether you're in the Top Quartile, Above Median, Below Median, or Bottom Quartile.
              </p>
              <div className="p-3 bg-[#1E293B] rounded-lg">
                <p className="text-xs text-[#64748B]">
                  <strong>Privacy Note:</strong> We publish cohort benchmarks only when 30+ similar merchants exist. 
                  This protects privacy and keeps benchmarks statistically meaningful.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Impact */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] mb-4">
                <strong className="text-white">What's this costing you?</strong> Estimated revenue impact from promise breaks (30-day window).
              </p>
              <div className="space-y-2 text-sm text-[#94A3B8]">
                <p><strong className="text-white">Estimated Monthly Loss:</strong> Leakage from cancellations, backorders, promise breaks, and support</p>
                <p><strong className="text-white">Potential Savings:</strong> If playbooks reduce breaks by your expected reduction %</p>
                <p><strong className="text-white">Annual Savings Potential:</strong> Projected yearly savings based on current trend</p>
              </div>
              <div className="mt-4 p-3 bg-[#1E293B] rounded-lg">
                <p className="text-xs text-[#64748B]">
                  <strong>Assumptions:</strong> You can edit GM%, Support cost/ticket, Tickets/disappointment, and Expected reduction 
                  to better match your business.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Upgrade Path */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Upgrade Path
        </h2>
        <div className="space-y-4">
          <Card className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-slate-600/30 text-slate-300 border-slate-500/30">Free</Badge>
                <h3 className="text-white font-semibold">Promise Health Check</h3>
              </div>
              <ul className="text-sm text-[#94A3B8] space-y-1">
                <li>• Promise Integrity Score (7/30 days)</li>
                <li>• Top promise-break drivers</li>
                <li>• Weekly digest (in-app)</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-sky-900/30 to-sky-800/20 border-sky-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Starter</Badge>
                <h3 className="text-white font-semibold">Ops Intelligence — $79/mo</h3>
              </div>
              <ul className="text-sm text-[#94A3B8] space-y-1">
                <li>• Everything in Free</li>
                <li>• Benchmark vs similar merchants</li>
                <li>• Impact estimator + editable assumptions</li>
                <li>• Trends dashboard (severity + driver distribution)</li>
                <li>• Exception Radar (view + basic capture)</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-emerald-900/30 to-emerald-800/20 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Pro</Badge>
                <h3 className="text-white font-semibold">Decision Graph + Slack — $299/mo</h3>
              </div>
              <ul className="text-sm text-[#94A3B8] space-y-1">
                <li>• Everything in Starter</li>
                <li>• Slack approvals (approve/reject in channel)</li>
                <li>• Approval queue + bulk re-request + snooze + reminders</li>
                <li>• Precedent library (mark/cite/apply)</li>
                <li>• Decision trace capture (Slack + UI)</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Next Steps */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          Next Steps
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/exception-radar">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-emerald-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Exception Radar →</h3>
                <p className="text-sm text-[#94A3B8]">Capture exceptions and build precedent</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/slack-integration">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-emerald-500/50 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Slack Integration →</h3>
                <p className="text-sm text-[#94A3B8]">Route approvals to Slack channels</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
